/**
 * Swift の `print` と viz 補助の出力形式を JS で再現する（記録の行末コメントと期待フレーム用）。
 */

/** `print([1, 2])` → `[1, 2]`。 */
export function swiftArray(items: readonly (number | string)[]): string {
  return `[${items.map((v) => (typeof v === 'string' ? JSON.stringify(v) : String(v))).join(', ')}]`;
}

/** `print(Optional(4))` → `Optional(4)`、`print(nil as Int?)` → `nil`。 */
export function swiftOptional(v: number | undefined): string {
  return v === undefined ? 'nil' : `Optional(${v})`;
}

/** Swift の文字列リテラル（キーは英小文字と数字だけなので、エスケープは JSON と同じでよい）。 */
export function swiftString(s: string): string {
  return JSON.stringify(s);
}

/**
 * viz 補助の並べ替え（viz-protocol.md §3.1、viz-helper.swift の less）。
 * 要素を JSON にした文字列どうしを比べ、両方が整数なら数として比べる。
 */
export function vizLess(a: string, b: string): number {
  const ia = /^-?\d+$/.test(a) ? Number(a) : null;
  const ib = /^-?\d+$/.test(b) ? Number(b) : null;
  if (ia !== null && ib !== null) return ia - ib;
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Set の viz 表現（昇順）。 */
export function vizSet(values: readonly number[]): number[] {
  return [...values].sort((a, b) => vizLess(JSON.stringify(a), JSON.stringify(b)));
}

/** Dictionary の viz 表現（`[[key, value], ...]`、キーで昇順）。 */
export function vizDict(entries: readonly (readonly [string, number])[]): [string, number][] {
  return entries
    .map(([k, v]): [string, number] => [k, v])
    .sort((x, y) => vizLess(JSON.stringify(x[0]), JSON.stringify(y[0])));
}

/** 値欄の文字列を整数にする。範囲外・整数でなければ null。 */
export const VALUE_MIN = -999;
export const VALUE_MAX = 999;
export function parseValue(text: string): number | null {
  const t = text.trim();
  if (!/^-?\d{1,3}$/.test(t)) return null;
  const n = Number(t);
  return n >= VALUE_MIN && n <= VALUE_MAX ? n : null;
}

/** 辞書のキー。英小文字で始まる 1〜8 文字の英小文字・数字。 */
export const KEY_PATTERN = /^[a-z][a-z0-9]{0,7}$/;
export function parseKey(text: string): string | null {
  const t = text.trim();
  return KEY_PATTERN.test(t) ? t : null;
}

/** 記録欄の 1 行（コードと行末コメント）。 */
export function formatEntry(entry: { code: string; result?: string }): string {
  return entry.result === undefined ? entry.code : `${entry.code}  // ${entry.result}`;
}
